/* Dropdown + MenuItem — popover menu with outside-click-to-close.
   Ported from lib/components.jsx (claude_design project 5c0836b6-facf-49dd-9c78-62afe3f7bc12). */

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface DropdownProps {
  trigger: React.ReactNode;
  children?: React.ReactNode;
  align?: 'left' | 'right';
  width?: number;
}

export function Dropdown({
  trigger,
  children,
  align = 'left',
  width = 200,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <div onClick={() => setOpen((o) => !o)}>{trigger}</div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            [align]: 0,
            width,
            zIndex: 70,
            background: 'var(--bg-canvas)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-pop)',
            padding: 5,
            animation: 'modal-in .14s ease',
          }}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export interface MenuItemProps {
  icon?: IconName;
  children?: React.ReactNode;
  danger?: boolean;
  onClick?: () => void;
  active?: boolean;
  right?: React.ReactNode;
  disabled?: boolean;
}

export function MenuItem({
  icon,
  children,
  danger,
  onClick,
  active,
  right,
  disabled,
}: MenuItemProps) {
  return (
    <button
      className="list-row"
      disabled={disabled}
      style={{
        padding: '7px 9px',
        fontSize: 'var(--fs-sm)',
        color: danger
          ? 'var(--red-text)'
          : active
          ? 'var(--accent-text)'
          : 'var(--text)',
        fontWeight: active ? 600 : 450,
        background: active ? 'var(--bg-selected)' : undefined,
        opacity: disabled ? 0.4 : undefined,
        cursor: disabled ? 'not-allowed' : undefined,
      }}
      onClick={onClick}
    >
      {icon && (
        <Icon
          name={icon}
          size={15}
          style={{ color: danger ? 'var(--red-text)' : 'var(--text-muted)' }}
        />
      )}
      <span className="grow">{children}</span>
      {right}
    </button>
  );
}

export default Dropdown;
